import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db, storage } from "./firebase";
import { ref, getDownloadURL } from "firebase/storage";
import apnagharLogo from "./apnaghar.png";
import "./AeroSearchUI.css";
import { Gem, ShieldCheck, Star, MapPin } from "lucide-react";
import LoadingScreen from "./LoadingScreen";
import { auth } from "./firebase";

const AeroSearchUI = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLoginDialog, setShowLoginDialog] = useState(false);

  const mode = searchParams.get("mode");
  const lat = parseFloat(searchParams.get("lat"));
  const lng = parseFloat(searchParams.get("lng"));
  const place = decodeURIComponent(searchParams.get("place") || "");

  const toRad = (val) => (val * Math.PI) / 180;
  const getDistanceKm = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  useEffect(() => {
    const fetchListings = async () => {
      setLoading(true);
      try {
        const collectionName = mode === "PG" ? "house-listings" : "rent-listings";
        const snapshot = await getDocs(collection(db, collectionName));

        const fetched = await Promise.all(
          snapshot.docs.map(async (doc) => {
            const data = { id: doc.id, ...doc.data() };
             data.listingId = data.listingId || doc.id;

            data.isPremium = data.isPremium === true || data.isPremium === "true";
            data.isVerified = data.isVerified === true || data.isVerified === "true";
            data.isBooked =
              data.isBooked === true || data.isBooked === "true" || data.booked === true;

            // ✅ Fetch thumbnail URL from storage if thumbnailPath exists
            if (data.thumbnailPath) {
              try {
                const url = await getDownloadURL(ref(storage, data.thumbnailPath));
                data.thumbnailURL = url;
              } catch (error) {
                console.warn("⚠️ Could not fetch image for:", data.thumbnailPath);
              }
            }

            // ✅ Extract coordinates from mapLink if available
            if (data.mapLink) {
              const match = data.mapLink.match(/q=(-?\d+\.?\d*),(-?\d+\.?\d*)/);
              if (match) {
                data.lat = parseFloat(match[1]);
                data.lng = parseFloat(match[2]);
              }
            }

            // ✅ Handle city, state, and nearbyRoads directly from Firestore
            data.city = data.city || "Unknown City";
            data.state = data.state || "Unknown State";
            data.nearbyRoads = Array.isArray(data.nearbyRoads)
              ? data.nearbyRoads
              : [];

            // ✅ Calculate distance from current location
            if (data.lat && data.lng) {
              const distance = getDistanceKm(lat, lng, data.lat, data.lng);
              data.distance = distance.toFixed(2);
            } else {
              data.distance = null;
            }

            return data;
          })
        );

        // ✅ Filter nearby and exclude booked PGs
        const nearby = fetched.filter((item) => {
          if (!item.lat || !item.lng) return false;
          if (item.showBooked === true) return false;
          return item.distance <= 7;
        });

        nearby.sort((a, b) => a.distance - b.distance);
        setListings(nearby);
      } catch (err) {
        console.error("Error fetching listings:", err);
      } finally {
        setLoading(false);
      }
    };

    if (lat && lng && mode) fetchListings();
  }, [lat, lng, mode]);

  const handleCardClick = (item) => {
    const user = auth.currentUser;
    if (!user) {
      setShowLoginDialog(true);
      return;
    }
    navigate(mode === "PG" ? `/pg/${item.id}` : `/rent/${item.id}`, {
      state: { data: item },
    });
  };

  return (
    <div className="aero-search-page">
      <button className="aero-back-btn" onClick={() => navigate(-1)}>
        ← Back
      </button>

      <div className="aero-header">
        <img src={apnagharLogo} alt="Logo" className="aero-logo" />
        <h1 className="aero-brand-name">ApkaGhr</h1>
      </div>

      <div className="aero-info">
        {loading ? (
          <LoadingScreen loading={loading} />
        ) : listings.length > 0 ? (
          <h2>
            Showing <span className="aero-highlight">{listings.length}</span> results near{" "}
            <span className="aero-highlight">{place}</span>
          </h2>
        ) : (
          <h2>No {mode} found near {place}</h2>
        )}
        <p className="aero-subtext">
          Explore verified, budget-friendly {mode}s within 7 km.
        </p>
      </div>

      {!loading && listings.length > 0 && (
        <div className="aero-list">
          {listings.map((item) => (
            <div
              key={item.id}
              className={`aero-card-wrapper ${
                item.isPremium ? "premium-card" : item.isVerified ? "verified-border" : ""
              }`}
              onClick={() => handleCardClick(item)}
            >
              <div className="aero-card">
                <div className="aero-image-wrapper">
                  <img
                    src={
                      item.thumbnailURL ||
                      item.imageUrl ||
                      item.images?.[0] ||
                      "https://via.placeholder.com/400x250?text=No+Image"
                    }
                    alt={item.pgName || "Listing"}
                  />
                  {item.distance && (
                    <span className="aero-distance">{item.distance} km away</span>
                  )}

                  {item.isPremium && (
                    <div className="aero-badge premium floating">
                      <Gem size={15} /> Featured
                    </div>
                  )}
                  {item.isVerified && !item.isPremium && (
                    <div className="aero-badge verified floating">
                      <ShieldCheck size={15} /> Verified
                    </div>
                  )}
                </div>

                <div className="aero-content">
                  <h4>{item.pgName || item.title}</h4>

                  {/* ✅ City & State */}
                  <p className="aero-location">
                    <MapPin size={14} /> {item.city}, {item.state}
                  </p>

               
                  <p>🏠 {item.occupancy || "Everyone"} | {item.roomType || "Room"}</p>
                  <p className="aero-price">₹ {item.rent || item.price} / month</p>
  <p className="listing-id">Listing ID: {item.listingId}</p>
                  {item.nearbyRoads.length > 0 && (
                    <p className="aero-nearby">
                      Nearby:{" "}
                      {item.nearbyRoads
                        .map((r) => `${r.name} (${r.distance})`)
                        .join(", ")}
                    </p>
                  )}

                  <div className="aero-rating">
                    <Star size={14} color="#FFD700" />
                    <span>{item.ratingAvg || 0}</span>
                    <small>({item.ratingCount || 0})</small>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showLoginDialog && (
        <div className="login-dialog-backdrop" onClick={() => setShowLoginDialog(false)}>
          <div className="login-dialog-box" onClick={(e) => e.stopPropagation()}>
            <img src={apnagharLogo} alt="ApkaGhr Logo" className="login-dialog-logo" />
            <h3>🔐 Please Sign In</h3>
            <p>Sign in to view full property details and contact owners.</p>
            <div className="login-dialog-buttons">
              <button onClick={() => navigate("/signin")} className="login-btn">
                Sign In
              </button>
              <button onClick={() => setShowLoginDialog(false)} className="close-btn">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AeroSearchUI;
